import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDailyActivity, startCleaning, markScanReady, getTeamMembers, getMe, getNotifications, markNotificationsSeen, getCleaningCompanies, setNextGuests, getMyCleaningCompany, markWelcomeSeen, getLateCheckouts, deleteLateCheckout, updateLateCheckout, getDueTasks, markOnboardingSeen } from '../lib/api';
import { cacheGet, cacheSet } from '../lib/cache';
import { getUser, setUser } from '../lib/auth';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, CheckCircle2, Clock, Loader2, CalendarDays, Bell, User, Play, Users, Shield, AlertTriangle, MapPin, SprayCan, LogOut, Minus, Plus, Sparkles, X, ListChecks } from 'lucide-react';
import StaffFilterSelect from '../components/StaffFilterSelect';
import MobileNav, { CheckoutsIcon } from '../components/MobileNav';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar } from '../components/ui/calendar';
import HomeNotifications from '../components/HomeNotifications';
import MaintenanceControl from '../components/MaintenanceControl';
import PullToRefresh from '../components/PullToRefresh';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { runOnboardingTour } from '../components/OnboardingTour';
import LateCheckoutDropdown from '../components/LateCheckoutDropdown';
import LateCheckoutModal from '../components/LateCheckoutModal';

export default function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [highlightId, setHighlightId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [staffFilter, setStaffFilter] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [cleaningGroups, setCleaningGroups] = useState([]);
  const [lateCheckouts, setLateCheckouts] = useState([]);
  const [dueTasks, setDueTasks] = useState([]);
  const [editingLate, setEditingLate] = useState(null);
  const calendarRef = useRef(null);
  const [user, setUserData] = useState(getUser());
  const [showWelcome, setShowWelcome] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'company_manager';
  const dismissWelcome = () => { setShowWelcome(false); markWelcomeSeen().catch(() => {}); };
  const canEditGuests = user?.role === 'admin' || user?.role === 'company_manager';
  const handleSetGuests = async (scanId, guests) => {
    setActivity((prev) => prev ? { ...prev, checkouts: prev.checkouts.map((c) => c.id === scanId ? { ...c, next_guests: guests } : c) } : prev);
    try { await setNextGuests(scanId, guests); } catch (e) { toast.error('Failed to update guest count'); }
  };
  const [photoUrl, setPhotoUrl] = useState(user?.photoUrl || null);
  const [photoError, setPhotoError] = useState(false);
  const greetingName = isAdmin ? (user?.companyName || 'there') : (user?.name || 'there');
  const [notif, setNotif] = useState({ bell: [], warning: [], bell_unseen: 0, warning_unseen: 0 });
  const [notifOpen, setNotifOpen] = useState(null);
  const notifRef = useRef(null);

  const loadNotif = async () => {
    try { const r = await getNotifications(); setNotif(r.data); } catch (e) { /* silent */ }
  };
  const toggleNotif = async (type) => {
    if (notifOpen === type) { setNotifOpen(null); return; }
    setNotifOpen(type);
    const key = type === 'bell' ? 'bell_unseen' : 'warning_unseen';
    if (notif[key] > 0) { setNotif((n) => ({ ...n, [key]: 0 })); try { await markNotificationsSeen(type); await loadNotif(); } catch (e) {} }
  };
  const openNotif = async (type) => {
    setNotifOpen(type);
    const key = type === 'bell' ? 'bell_unseen' : 'warning_unseen';
    if (notif[key] > 0) { setNotif((n) => ({ ...n, [key]: 0 })); try { await markNotificationsSeen(type); await loadNotif(); } catch (e) {} }
  };

  useEffect(() => {
    getMe().then((res) => {
      const fresh = res.data || {};
      const merged = { ...getUser(), ...fresh };
      setPhotoUrl(fresh.photoUrl || null);
      setPhotoError(false);
      setUser(merged);
      setUserData(merged);
      if (fresh.hasSeenWelcome === false) setShowWelcome(true);
      if (isAdmin && fresh.hasSeenOnboarding === false) {
        // First time ONLY. Mark onboarding seen immediately so it is tied to the
        // account on the server and never reappears again on any device.
        // Then run the guided tour and reveal the setup checklist for this session.
        markOnboardingSeen().catch(() => {});
        setTimeout(() => {
          try { runOnboardingTour(() => setShowChecklist(true)); }
          catch (e) { setShowChecklist(true); }
        }, 900);
      }
    }).catch(() => {});
    loadNotif();
  }, []);

  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  const loadActivity = async () => {
    const staffKey = (isAdmin || isManager) && staffFilter !== 'all' ? staffFilter : 'all';
    const cacheKey = `activity:${formatDateForAPI(selectedDate)}:${staffKey}`;
    const cached = cacheGet(cacheKey);
    if (cached) { setActivity(cached); setLoading(false); } else { setLoading(true); }
    try {
      const response = await getDailyActivity(
        formatDateForAPI(selectedDate),
        (isAdmin || isManager) && staffFilter !== 'all' ? staffFilter : null
      );
      setActivity(response.data);
      cacheSet(cacheKey, response.data);
    } catch (error) {
      if (!cached) toast.error('Failed to load daily activity');
    } finally {
      setLoading(false);
    }
  };

  const loadLateCheckouts = async () => {
    try {
      const r = await getLateCheckouts(formatDateForAPI(selectedDate));
      setLateCheckouts(r.data.lateCheckouts || []);
    } catch (e) { /* silent */ }
  };

  const loadDueTasks = async () => {
    try {
      const r = await getDueTasks(formatDateForAPI(selectedDate));
      setDueTasks(r.data.tasks || []);
    } catch (e) { /* silent */ }
  };

  const handleDeleteLateCheckout = async (id) => {
    try {
      await deleteLateCheckout(id);
      setLateCheckouts((prev) => prev.filter((x) => x.id !== id));
      toast.success('Late checkout removed');
    } catch (e) {
      toast.error('Failed to remove late checkout');
    }
  };

  const handleEditLateCheckout = (lc) => setEditingLate(lc);

  const handleUpdateLateCheckout = async (id, payload) => {
    await updateLateCheckout(id, payload);
    await loadLateCheckouts();
  };

  useEffect(() => {
    loadActivity();
    loadLateCheckouts();
    loadDueTasks();
  }, [selectedDate, staffFilter]);

  useAutoRefresh(() => { loadActivity(); loadNotif(); loadLateCheckouts(); loadDueTasks(); });

  // A new-checkout toast tap arrives as /home?focus=<id> — jump to today and highlight it.
  useEffect(() => {
    const focus = new URLSearchParams(location.search).get('focus');
    if (!focus) return;
    setSelectedDate(new Date());
    setHighlightId(focus);
    navigate('/home', { replace: true });
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [location.search]);

  useEffect(() => {
    if (isAdmin) {
      getTeamMembers()
        .then((res) => setStaffList((res.data.members || []).filter((m) => m.role === 'staff')))
        .catch(() => {});
      getCleaningCompanies()
        .then((res) => {
          const groups = (res.data.companies || [])
            .map((c) => ({ id: c.id, name: c.name, cleaners: c.cleaners || [] }))
            .filter((g) => g.cleaners.length > 0);
          setCleaningGroups(groups);
        })
        .catch(() => {});
    } else if (isManager) {
      getMyCleaningCompany()
        .then((res) => setStaffList(res.data.cleaners || []))
        .catch(() => {});
    }
  }, [isAdmin, isManager]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    if (newDate <= new Date()) {
      setSelectedDate(newDate);
    }
  };

  const handleDateSelect = (date) => {
    if (date && date <= new Date()) {
      setSelectedDate(date);
      setShowCalendar(false);
    }
  };

  const handleStartCleaning = async (scanId) => {
    setActionLoading(scanId);
    try {
      await startCleaning(scanId);
      toast.success('Cleaning started!');
      loadActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start cleaning');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkReady = async (scanId) => {
    setActionLoading(scanId);
    try {
      await markScanReady(scanId);
      toast.success('Unit marked as ready!');
      loadActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as ready');
    } finally {
      setActionLoading(null);
    }
  };

  const isToday = () => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  const formatDateDisplay = () => {
    return selectedDate.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short'
    });
  };

  const formatDateFull = () => {
    return selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const canGoNext = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return selectedDate < today && !isToday();
  };

  return (
    <PullToRefresh onRefresh={async () => { await Promise.all([loadActivity(), loadNotif(), loadLateCheckouts(), loadDueTasks()]); }}>
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <MobileNav />

      <main className="flex-1 px-4 pt-4 pb-28 md:pb-8 max-w-lg mx-auto w-full">
        {isAdmin && showChecklist && <OnboardingChecklist />}
        {showWelcome && !isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 rounded-2xl p-4 flex items-center gap-3 text-white shadow-lg"
            style={{ background: 'linear-gradient(135deg,#3B82F6,#10B981)' }}
            data-testid="welcome-banner"
          >
            <Sparkles size={20} className="flex-shrink-0" />
            <button
              onClick={async () => { try { await markWelcomeSeen(); } catch (e) {} window.location.href = '/guide'; }}
              className="flex-1 text-left"
              data-testid="welcome-banner-cta"
            >
              <p className="text-sm font-bold leading-tight">Welcome to GuestCheckout! 👋</p>
              <p className="text-xs text-white/90 mt-0.5">Tap here to see our quick guide and get started.</p>
            </button>
            <button
              onClick={dismissWelcome}
              data-testid="welcome-banner-dismiss"
              className="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              aria-label="Dismiss"
            >
              <X size={15} />
            </button>
          </motion.div>
        )}
        {/* Header: greeting + notifications */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm ring-1 ring-slate-100"
                title={user?.name || user?.email}
                data-testid="home-user-photo"
              >
                {photoUrl && !photoError ? (
                  <img src={photoUrl} alt={greetingName} className="w-full h-full object-cover" onError={() => setPhotoError(true)} />
                ) : (
                  (greetingName || '?').trim().charAt(0).toUpperCase()
                )}
              </div>
              <h1 className="text-lg font-bold text-slate-900 truncate" data-testid="home-greeting">
                Hello {greetingName}
              </h1>
            </div>
            <HomeNotifications data={notif} open={notifOpen} onToggle={toggleNotif} innerRef={notifRef} />
          </div>
        </motion.div>

        {/* Heading + compact date stepper */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex items-center justify-between gap-4 mb-4"
        >
          <h2 className="text-2xl font-extrabold gradient-text" data-testid="daily-activity-heading">Daily activity</h2>
          <div className="flex items-center gap-0.5 bg-white rounded-xl px-1 py-1 shadow-sm border border-slate-100 relative" ref={calendarRef}>
            <button onClick={handlePrevDay} className="w-7 h-7 rounded-lg bg-slate-50 hover:bg-slate-100 flex items-center justify-center transition-all active:scale-95" data-testid="prev-day-button">
              <ChevronLeft size={15} className="text-slate-600" />
            </button>
            <button
              onClick={() => !isToday() && setSelectedDate(new Date())}
              disabled={isToday()}
              className={`text-xs font-semibold px-1 transition-colors ${isToday() ? 'text-slate-300 cursor-default' : 'text-emerald-600 hover:text-emerald-700 cursor-pointer'}`}
              data-testid="today-button"
            >
              Today
            </button>
            <button onClick={() => setShowCalendar(!showCalendar)} className="flex items-center gap-1 px-1.5 h-7 rounded-lg hover:bg-slate-50 transition-colors" data-testid="date-display">
              <span className="text-sm font-bold text-slate-900 whitespace-nowrap">{formatDateDisplay()}</span>
              <CalendarDays size={14} className="text-emerald-500" />
            </button>
            <button onClick={handleNextDay} disabled={!canGoNext()} className="w-7 h-7 rounded-lg bg-slate-50 hover:bg-slate-100 flex items-center justify-center transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="next-day-button">
              <ChevronRight size={15} className="text-slate-600" />
            </button>

            {/* Calendar Popup */}
            <AnimatePresence>
              {showCalendar && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute top-full right-0 mt-2 z-50"
                >
                  <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-2 max-w-[calc(100vw-2rem)]">
                    <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} disabled={(date) => date > new Date()} className="rounded-xl" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Filters row: Late checkouts (wider) + staff filter (narrower) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className={`mb-6 grid gap-2.5 ${(isAdmin || isManager) && (staffList.length > 0 || cleaningGroups.length > 0) ? 'grid-cols-5' : 'grid-cols-1'}`}
        >
          <div className={(isAdmin || isManager) && (staffList.length > 0 || cleaningGroups.length > 0) ? 'col-span-3' : ''}>
            <LateCheckoutDropdown
              items={lateCheckouts}
              canDelete={isAdmin}
              canEdit={isAdmin}
              onDelete={handleDeleteLateCheckout}
              onEdit={handleEditLateCheckout}
            />
          </div>
          {(isAdmin || isManager) && (staffList.length > 0 || cleaningGroups.length > 0) && (
            <div className="col-span-2">
              <StaffFilterSelect
                value={staffFilter}
                onChange={setStaffFilter}
                staffList={staffList}
                cleaningGroups={cleaningGroups}
                showGroups={isAdmin}
                individualLabel={isManager ? 'My cleaners' : 'My team'}
              />
            </div>
          )}
        </motion.div>

        <LateCheckoutModal
          open={!!editingLate}
          editing={editingLate}
          onClose={() => setEditingLate(null)}
          onUpdate={handleUpdateLateCheckout}
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-emerald-500" />
          </div>
        ) : activity && activity.checkouts.length === 0 ? (
          /* Empty State */
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-8 text-center shadow-sm border border-slate-100"
          >
            <div className="w-20 h-20 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <CheckoutsIcon size={36} className="text-emerald-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No checkouts</h3>
            <p className="text-slate-500 text-sm">
              No activity {isToday() ? 'today yet' : `on ${formatDateDisplay()}`}
            </p>
          </motion.div>
        ) : activity && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-2.5 mb-6">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
                data-testid="stat-checked-out"
              >
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center mb-2.5">
                  <CheckoutsIcon size={19} className="text-blue-500" strokeWidth={1.8} />
                </div>
                <p className="text-3xl font-bold text-slate-900 leading-none">{activity.stats.checked_out}</p>
                <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mt-1.5 block">Checked out</span>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
                data-testid="stat-cleaning"
              >
                <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center mb-2.5">
                  <SprayCan size={18} className="text-amber-500" />
                </div>
                <p className="text-3xl font-bold text-slate-900 leading-none">{activity.is_past ? activity.stats.not_marked : activity.stats.still_cleaning}</p>
                <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mt-1.5 block">
                  Cleaning
                </span>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
                data-testid="stat-ready"
              >
                <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center mb-2.5">
                  <CheckCircle2 size={18} className="text-emerald-500" />
                </div>
                <p className="text-3xl font-bold text-slate-900 leading-none">{activity.stats.ready}</p>
                <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mt-1.5 block">Ready</span>
              </motion.div>
            </div>

            {/* Checkout List */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-3 px-1">Checkout Log</h3>
              <div className="space-y-2">
                <AnimatePresence>
                  {(() => {
                    const tasksByProp = {};
                    const tasksByName = {};
                    dueTasks.forEach((t) => {
                      (tasksByProp[t.propertyId] = tasksByProp[t.propertyId] || []).push(t);
                      if (t.propertyName) (tasksByName[t.propertyName] = tasksByName[t.propertyName] || []).push(t);
                    });
                    return [...activity.checkouts]
                    .sort((a, b) => {
                      const rank = (c) => (c.overdue ? 2 : 0) + (c.carried_over ? 1 : 0);
                      return rank(b) - rank(a);
                    })
                    .map((checkout, index) => (
                    <CheckoutRow 
                      key={checkout.id}
                      checkout={checkout}
                      isAdmin={isAdmin}
                      isPast={activity.is_past}
                      onStartCleaning={handleStartCleaning}
                      onMarkReady={handleMarkReady}
                      isLoading={actionLoading === checkout.id}
                      index={index}
                      canEditGuests={canEditGuests}
                      onSetGuests={handleSetGuests}
                      maintenanceEnabled={activity.maintenance_enabled}
                      onMaintenanceSubmitted={loadActivity}
                      highlightId={highlightId}
                      tasks={tasksByProp[checkout.propertyId] || tasksByName[checkout.propertyName] || []}
                    />
                  ));
                  })()}
                </AnimatePresence>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
    </PullToRefresh>
  );
}

function CheckoutRow({ checkout, isAdmin, isPast, onStartCleaning, onMarkReady, isLoading, index, canEditGuests, onSetGuests, maintenanceEnabled, onMaintenanceSubmitted, highlightId, tasks = [] }) {
  const [showNotified, setShowNotified] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [confirmReady, setConfirmReady] = useState(false);
  const [checkedTasks, setCheckedTasks] = useState({});
  const rowRef = useRef(null);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (highlightId && checkout.id === highlightId) {
      setFlash(true);
      try { rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
      const t = setTimeout(() => setFlash(false), 2600);
      return () => clearTimeout(t);
    }
  }, [highlightId, checkout.id]);
  
  const formatTime = (isoString, tz) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    try {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz || undefined });
    } catch (e) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
  };

  const tzAbbr = (tz) => {
    if (!tz) return '';
    try {
      return new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || '';
    } catch (e) {
      return '';
    }
  };

  const formatDay = (isoString, tz) => {
    if (!isoString) return '';
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: tz || undefined })
        .formatToParts(new Date(isoString));
      const wd = parts.find((p) => p.type === 'weekday')?.value || '';
      const d = parts.find((p) => p.type === 'day')?.value || '';
      const m = parts.find((p) => p.type === 'month')?.value || '';
      return `${wd} ${d}/${m}`;
    } catch (e) {
      return '';
    }
  };

  const hasCheckedOut = checkout.checked_out_at || checkout.status === 'accepted';
  const cleaningStarted = !!checkout.cleaning_started_at;
  const isReady = !!checkout.ready_at;
  const notifiedUsers = checkout.notifiedUsers || [];

  // Determine the status for display
  const getStatus = () => {
    if (isReady) return 'ready';
    if (cleaningStarted) return 'cleaning';
    return 'awaiting';
  };

  const status = getStatus();
  const isOverdue = !!checkout.overdue && status === 'cleaning';

  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isOverdue) return;
    const t = setInterval(() => setTick((x) => x + 1), 60000);
    return () => clearInterval(t);
  }, [isOverdue]);

  const fmtOver = (m) => {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return h ? `${h}h ${r}m` : `${r}m`;
  };
  const overdueMinutes = isOverdue && checkout.cleaning_started_at && checkout.target_minutes
    ? Math.max(0, Math.round((Date.now() - new Date(checkout.cleaning_started_at).getTime()) / 60000) - checkout.target_minutes)
    : 0;

  const statusStyle = isOverdue
    ? { border: 'border-red-300/80', glow: '0 2px 14px -6px rgba(239,68,68,0.45)' }
    : {
        awaiting: { border: 'border-blue-200/70', glow: '0 2px 14px -6px rgba(59,130,246,0.35)' },
        cleaning: { border: 'border-amber-200/80', glow: '0 2px 14px -6px rgba(245,158,11,0.4)' },
        ready: { border: 'border-emerald-200/80', glow: '0 2px 14px -6px rgba(16,185,129,0.4)' },
      }[status];

  return (
    <motion.div 
      ref={rowRef}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className={`bg-white rounded-xl px-3.5 py-2.5 border transition-all ${statusStyle.border} ${flash ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
      style={{ boxShadow: statusStyle.glow }}
      data-testid={`checkout-row-${checkout.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 min-w-0">
            <p className="font-semibold text-slate-900 text-lg truncate">{checkout.propertyName}</p>
            {checkout.propertyAddress && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 min-w-0 flex-shrink max-w-[45%]" data-testid={`checkout-address-${checkout.id}`}>
                <MapPin size={11} className="text-slate-400 flex-shrink-0" />
                <span className="truncate">{checkout.propertyAddress}</span>
              </span>
            )}
          </div>
          {isOverdue && (
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100 text-xs font-semibold" data-testid={`overdue-${checkout.id}`}>
              <AlertTriangle size={11} /> Overdue
              <span className="text-red-500 font-bold" data-testid={`overdue-time-${checkout.id}`}>· +{fmtOver(overdueMinutes)}</span>
            </span>
          )}
        </div>

        {/* Status Badge or Action Buttons */}
        <div className="flex-shrink-0">
          {isAdmin ? (
            /* Admin: Read-only badges */
            status === 'ready' ? (
              <div className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-emerald-50 text-emerald-600 rounded-full text-sm font-semibold border border-emerald-100">
                <CheckCircle2 size={15} />
                <span>Ready</span>
              </div>
            ) : status === 'cleaning' ? (
              <div className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-amber-50 text-amber-600 rounded-full text-sm font-semibold border border-amber-100">
                <Clock size={15} />
                <span>Cleaning</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-100 text-slate-500 rounded-full text-sm font-semibold">
                <Clock size={15} />
                <span>Awaiting</span>
              </div>
            )
          ) : (
            /* Staff: Action buttons */
            status === 'ready' ? (
              <div className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-emerald-50 text-emerald-600 rounded-full text-sm font-semibold border border-emerald-100">
                <CheckCircle2 size={15} />
                <span>Ready</span>
              </div>
            ) : status === 'cleaning' ? (
              <button
                onClick={() => { if (tasks.length > 0) { setCheckedTasks({}); setConfirmReady(true); } else { onMarkReady(checkout.id); } }}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 text-white rounded-xl text-sm font-semibold transition-all active:scale-95 disabled:opacity-50 shadow-sm"
                data-testid={`mark-ready-${checkout.id}`}
              >
                {isLoading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={15} />
                )}
                <span>Mark Ready</span>
              </button>
            ) : (
              <button
                onClick={() => onStartCleaning(checkout.id)}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold transition-all active:scale-95 disabled:opacity-50 shadow-sm"
                data-testid={`start-cleaning-${checkout.id}`}
              >
                {isLoading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Play size={15} />
                )}
                <span>Start Cleaning</span>
              </button>
            )
          )}
        </div>
      </div>

      {/* Bottom row: status timeline badges + next check-in guests */}
      <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-slate-50">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold whitespace-nowrap border ${checkout.carried_over ? 'border-red-400' : 'border-blue-100'}`}
            title={checkout.carried_over ? 'Checked out on a previous day — still to finish' : undefined}
            data-testid={`checkedout-info-${checkout.id}`}
          >
            <LogOut size={11} /> {formatTime(checkout.checked_out_at || checkout.timestamp, checkout.timezone)}{checkout.carried_over ? ` · ${formatDay(checkout.checked_out_at || checkout.timestamp, checkout.timezone)}` : ''}
          </span>
          {cleaningStarted && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-xs font-semibold whitespace-nowrap" data-testid={`started-info-${checkout.id}`}>
              <SprayCan size={11} /> {formatTime(checkout.cleaning_started_at, checkout.timezone)}{checkout.cleaning_started_by ? ` · ${checkout.cleaning_started_by}` : ''}
            </span>
          )}
          {isReady && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-semibold whitespace-nowrap" data-testid={`ready-info-${checkout.id}`}>
              <CheckCircle2 size={11} /> {formatTime(checkout.ready_at, checkout.timezone)}{checkout.ready_by ? ` · ${checkout.ready_by}` : ''}
            </span>
          )}
        </div>
        <div className="flex-shrink-0">
          <GuestsControl checkout={checkout} canEdit={canEditGuests} onSet={onSetGuests} />
        </div>
      </div>

      {/* Footer: notified + maintenance + daily tasks (single compact row) */}
      {(notifiedUsers.length > 0 || maintenanceEnabled || tasks.length > 0) && (
        <div className="mt-1.5 pt-1.5 border-t border-slate-50 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {tasks.length > 0 && (
            <button
              onClick={() => setShowTasks((v) => !v)}
              className="flex items-center gap-1.5 text-sm leading-none text-red-600 hover:text-red-700 transition-colors"
              data-testid={`daily-task-badge-${checkout.id}`}
            >
              <span className="relative flex items-center justify-center h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              <span>Daily Task{tasks.length > 1 ? 's' : ''} · {tasks.length}</span>
              <ChevronRight size={14} className={`shrink-0 transition-transform ${showTasks ? 'rotate-90' : ''}`} />
            </button>
          )}
          {notifiedUsers.length > 0 && (
            <>
              <button 
                onClick={() => setShowNotified(!showNotified)}
                className="flex items-center gap-1.5 text-sm leading-none text-slate-500 hover:text-slate-700 transition-colors"
              >
                <Bell size={14} />
                <span>{notifiedUsers.length} notified</span>
                <ChevronRight size={14} className={`transition-transform ${showNotified ? 'rotate-90' : ''}`} />
              </button>
              <AnimatePresence>
                {showNotified && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="w-full order-last overflow-hidden"
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {notifiedUsers.map((user, idx) => (
                        <span 
                          key={idx}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${
                            user.role === 'admin' 
                              ? 'bg-amber-50 text-amber-700' 
                              : 'bg-blue-50 text-blue-700'
                          }`}
                        >
                          {user.role === 'admin' ? <Shield size={13} /> : <User size={13} />}
                          {user.name}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
          <MaintenanceControl checkout={checkout} enabled={maintenanceEnabled} onSubmitted={onMaintenanceSubmitted} className="ml-auto" />
          <AnimatePresence>
            {showTasks && tasks.length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="w-full order-last overflow-hidden">
                <div className="rounded-xl border border-red-100 bg-red-50/40 p-2.5 space-y-1.5" data-testid={`daily-task-popup-${checkout.id}`}>
                  <p className="text-[11px] font-bold text-red-500 uppercase tracking-wide">Tasks for this day</p>
                  {tasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 text-xs text-slate-700">
                      <ListChecks size={13} className="text-red-500 flex-shrink-0" />
                      <span className="font-semibold">{t.title}</span>
                      <span className="text-slate-400 ml-auto">{t.time}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Mark Ready confirmation with task ticks */}
      <AnimatePresence>
        {confirmReady && (
          <motion.div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setConfirmReady(false)} />
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="relative w-full sm:max-w-sm sm:m-4 bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden" data-testid={`ready-confirm-${checkout.id}`}>
              <div className="px-5 py-4 bg-gradient-to-r from-blue-500 to-emerald-500 text-white">
                <h3 className="font-bold text-base leading-tight">Before you mark it Ready</h3>
                <p className="text-xs text-white/80 mt-0.5">Tick the tasks you've done at {checkout.propertyName}</p>
              </div>
              <div className="p-5 space-y-2">
                {tasks.map((t) => (
                  <label key={t.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors" data-testid={`ready-task-${t.id}`}>
                    <input type="checkbox" checked={!!checkedTasks[t.id]} onChange={(e) => setCheckedTasks((p) => ({ ...p, [t.id]: e.target.checked }))} className="w-5 h-5 accent-emerald-500 flex-shrink-0" />
                    <span className="text-sm font-semibold text-slate-800 min-w-0 truncate">{t.title}</span>
                    <span className="text-xs text-slate-400 ml-auto flex-shrink-0">{t.time}</span>
                  </label>
                ))}
                <button
                  disabled={!tasks.every((t) => checkedTasks[t.id]) || isLoading}
                  onClick={() => { setConfirmReady(false); onMarkReady(checkout.id); }}
                  data-testid={`ready-confirm-btn-${checkout.id}`}
                  className="w-full h-12 mt-2 rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.99]"
                >
                  <CheckCircle2 size={18} /> Confirm Flat Ready
                </button>
                <p className="text-[11px] text-slate-400 text-center">Tick every task to continue.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function GuestsControl({ checkout, canEdit, onSet }) {
  const [val, setVal] = useState(checkout.next_guests || 0);
  useEffect(() => { setVal(checkout.next_guests || 0); }, [checkout.next_guests]);
  const commit = (n) => {
    const clamped = n < 0 ? 0 : n > 20 ? 20 : n;
    setVal(clamped);
    onSet(checkout.id, clamped === 0 ? null : clamped);
  };
  if (!canEdit) {
    if (!val) return null;
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs font-semibold whitespace-nowrap" title="Guests arriving for the next check-in" data-testid={`guests-readonly-${checkout.id}`}>
        <Users size={12} /> {val} <span className="font-normal opacity-70">next</span>
      </span>
    );
  }
  if (!val) {
    return (
      <button
        onClick={() => commit(1)}
        title="Optional — guests arriving for the next check-in (only shown to cleaners if you add it)"
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 text-slate-500 border border-dashed border-slate-300 text-xs font-semibold hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-300 transition-colors whitespace-nowrap"
        data-testid={`guests-add-${checkout.id}`}
      >
        <Users size={12} /><Plus size={11} /> Next guests <span className="font-normal text-slate-400">· optional</span>
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5" title="Guests arriving for the next check-in">
      <span className="text-[9px] leading-none uppercase tracking-wide text-slate-400 font-bold">Next<br/>check-in</span>
      <span className="inline-flex items-center gap-1 pl-1 pr-1 py-0.5 rounded-full bg-indigo-50 border border-indigo-100" data-testid={`guests-stepper-${checkout.id}`}>
        <button onClick={() => commit(val - 1)} className="w-6 h-6 rounded-full bg-white text-indigo-600 flex items-center justify-center shadow-sm active:scale-90 transition-transform" data-testid={`guests-minus-${checkout.id}`}><Minus size={13} /></button>
        <span className="inline-flex items-center gap-1 text-indigo-700 text-xs font-bold min-w-[40px] justify-center" data-testid={`guests-value-${checkout.id}`}><Users size={12} /> {val}</span>
        <button onClick={() => commit(val + 1)} className="w-6 h-6 rounded-full bg-white text-indigo-600 flex items-center justify-center shadow-sm active:scale-90 transition-transform" data-testid={`guests-plus-${checkout.id}`}><Plus size={13} /></button>
      </span>
    </span>
  );
}
